/**
 * Pure helpers for the Tasks worklist filters: building the dialog sections,
 * mapping a user selection to the server-side filter payload, rebuilding a
 * FilterValues object (for Redux chips/dialog), and (de)serialising the
 * selection to the URL query string so a filtered view is shareable.
 */

import moment from "moment";

import {
  FilterOptionsResponse,
  TaskWorklistFilters,
} from "../../../api/tasks/taskTypes";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";

/** Redux/table key for the Tasks worklist (must match the CustomTable tableName). */
export const TASKS_FILTER_NAME = "pharmacyTasks";

/** Query param that carries the shareable filter selection. */
const FILTERS_PARAM = "f";

/** A minimal, URL-serialisable selection map: section id -> selected values. */
export type TasksFilterSelection = Record<string, string[]>;

const toOptions = (values: string[]) =>
  values.map((value) => ({ label: value, value }));

/**
 * Build the filter dialog sections from server-observed facet values.
 *
 * Server-driven facets are kept only when they have observed values; the
 * static (priority / CMM-key), date-range, and disabled placeholder sections
 * are always kept.
 *
 * @param options - Distinct facet values from the filter-options endpoint
 * @returns Ordered filter sections for the dialog
 */
export function buildTaskFilterSections(
  options: FilterOptionsResponse,
): FilterSection[] {
  const sections: FilterSection[] = [
    {
      id: "inbox",
      label: "Inbox",
      type: "string",
      options: options.inboxes.map((inbox) => ({
        label: inbox.name || inbox.recipient_user_id,
        value: inbox.recipient_user_id,
      })),
    },
    {
      id: "task_status",
      label: "Task Status",
      type: "string",
      options: toOptions(options.task_statuses),
    },
    {
      id: "bucket",
      label: "Task Category",
      type: "string",
      options: toOptions(options.buckets),
    },
    {
      id: "priority",
      label: "Priority",
      type: "string",
      options: [{ label: "Priority only", value: "true" }],
    },
    {
      id: "cmm_key",
      label: "CMM Key",
      type: "string",
      options: [
        { label: "Has CMM key", value: "yes" },
        { label: "No CMM key", value: "no" },
      ],
    },
    {
      id: "outcome",
      label: "Outcome",
      type: "string",
      options: toOptions(options.outcome_statuses),
    },
    {
      id: "pa_status",
      label: "PA Status",
      type: "string",
      options: toOptions(options.pa_statuses),
    },
    {
      id: "drug",
      label: "Drug",
      type: "string",
      options: toOptions(options.drugs),
    },
    { id: "last_activity", label: "Last Activity", type: "date", options: [] },
    { id: "created", label: "Created At", type: "date", options: [] },
    {
      id: "assignee",
      label: "Assignee",
      type: "string",
      options: [],
      disabled: true,
      note: "Available once add-recipient ships (PR2).",
    },
  ];

  return sections.filter(
    (section) =>
      section.options.length > 0 || section.type === "date" || section.disabled,
  );
}

/** Reduce a FilterValues.values object to the minimal id -> values map. */
export function toSelection(
  values: FilterValues["values"],
): TasksFilterSelection {
  const selection: TasksFilterSelection = {};
  Object.entries(values ?? {}).forEach(([id, entry]) => {
    if (entry?.values?.length) selection[id] = entry.values;
  });
  return selection;
}

const startOfDayIso = (value?: string): string | null =>
  value ? moment(value, "MM/DD/YYYY").startOf("day").toISOString() : null;
const endOfDayIso = (value?: string): string | null =>
  value ? moment(value, "MM/DD/YYYY").endOf("day").toISOString() : null;

const cmmKeyPresent = (values: string[]): boolean | null => {
  const yes = values.includes("yes");
  const no = values.includes("no");
  if (yes === no) return null; // both or neither -> no constraint
  return yes;
};

/**
 * Map a selection to the server-side worklist filter payload.
 *
 * @param selection - Section id -> selected values map
 * @returns Partial worklist filters ready to send to the backend
 */
export function toServerFilters(
  selection: TasksFilterSelection,
): Partial<TaskWorklistFilters> {
  const lastActivity = selection.last_activity ?? [];
  const created = selection.created ?? [];
  return {
    inbox_recipient_user_ids: selection.inbox ?? [],
    task_statuses: selection.task_status ?? [],
    buckets: selection.bucket ?? [],
    outcome_statuses: selection.outcome ?? [],
    pa_statuses: selection.pa_status ?? [],
    drugs: selection.drug ?? [],
    priority_only: (selection.priority ?? []).includes("true"),
    cmm_key_present: cmmKeyPresent(selection.cmm_key ?? []),
    last_activity_from: startOfDayIso(lastActivity[0]),
    last_activity_to: endOfDayIso(lastActivity[1]),
    created_from: startOfDayIso(created[0]),
    created_to: endOfDayIso(created[1]),
  };
}

/**
 * Rebuild a FilterValues object (for Redux chips / dialog) from a selection.
 *
 * @param selection - Section id -> selected values map
 * @param sections - Current dialog sections (for labels + types)
 * @returns FilterValues keyed to the Tasks worklist
 */
export function toFilterValues(
  selection: TasksFilterSelection,
  sections: FilterSection[],
): FilterValues {
  const values: FilterValues["values"] = {};
  let filterCount = 0;
  Object.entries(selection).forEach(([id, vals]) => {
    if (!vals?.length) return;
    const section = sections.find((sec) => sec.id === id);
    const type = section?.type ?? "string";
    const valuesLabel =
      type === "date"
        ? vals.map((value) => moment(value, "MM/DD/YYYY").format("DD MMM YYYY"))
        : vals.map(
            (value) =>
              section?.options.find((opt) => opt.value === value)?.label ??
              value,
          );
    values[id] = {
      values: vals,
      valuesLabel,
      type,
      label: section?.label ?? id,
    };
    filterCount += 1;
  });
  return { name: TASKS_FILTER_NAME, values, filterCount };
}

/** Parse the shareable filter selection from URL query params. */
export function parseTasksSelection(
  params: URLSearchParams,
): TasksFilterSelection {
  const raw = params.get(FILTERS_PARAM);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
   
    const selection: TasksFilterSelection = {};
    Object.entries(parsed).forEach(([id, vals]) => {
      if (Array.isArray(vals) && vals.every((v) => typeof v === "string")) {
        selection[id] = vals as string[];
      }
    });
    return selection;
  } catch {
    return {};
  }
}

/** Write the filter selection into a clone of the given params (returns the clone). */
export function withFiltersParam(
  params: URLSearchParams,
  selection: TasksFilterSelection,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (Object.keys(selection).length) {
    next.set(FILTERS_PARAM, JSON.stringify(selection));
  } else {
    next.delete(FILTERS_PARAM);
  }
  return next;
}
